import { useApi } from '@/api';
import { Link, TableSkeleton } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  AnimatedCurrencyValue,
  CryptoIcon,
  CurrencyValue,
  Flex,
  WalletAddress,
  DataTable,
  RelativeDateTime,
  Box
} from '@blockanalitica/ui';
import { farmMapping } from '@/utils/farms';

export default function DepositorsTable({
  address,
  pagination,
  sorting,
  search
}) {
  const [data, error] = useApi({
    url: `/farms/${address}/depositors-sky/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      search: search
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Wallet',
        getCell: (row) => (
          <WalletAddress
            address={row.wallet_address}
            options={{
              inline: true,
              sizes: { blockie: '0' }
            }}
          />
        ),
        columnSize: ['minmax(100px, auto)', 'minmax(150px, auto)']
      },
      {
        header: 'Urn',
        getCell: (row) => (
          <WalletAddress
            address={row.urn_address}
            options={{
              inline: true,
              showBlockie: false
            }}
          />
        )
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Supplied
            <CryptoIcon
              name={data.staking_token_symbol}
              address={data.staking_token_address}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => (
          <CurrencyValue
            value={row.balance}
            options={{
              iconSize: '0'
            }}
          />
        ),
        getCellBottom: (row) => (
          <CurrencyValue
            color="muted"
            fontSize="-1"
            value={row.balance_usd}
            options={{
              currency: 'USD'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'balance'
      },
      {
        header:
          farmMapping(address) !== 'chronicle_farm' ? (
            <Flex
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="3xs">
              Estimated Rewards
              <CryptoIcon
                name={data.rewards_token_symbol}
                address={data.rewards_token_address}
                size="0"
              />
            </Flex>
          ) : (
            <Flex
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="3xs">
              Estimated Points
            </Flex>
          ),
        getCell: (row) => (
          <AnimatedCurrencyValue
            value={row.reward_balance}
            endlessPerSecond={row.reward_tokens_per_second}
          />
        ),
        getCellBottom: (row) =>
          farmMapping(address) !== 'chronicle_farm' ? (
            <CurrencyValue
              color="muted"
              fontSize="-1"
              value={row.reward_balance_usd}
              options={{
                currency: 'USD'
              }}
            />
          ) : null,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'reward_balance'
      },
      {
        header: 'Latest Activity',
        getCell: (row) => (
          <Box color="muted">
            <RelativeDateTime value={row.datetime} />
          </Box>
        ),
        sortingField: 'datetime',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    if (farmMapping(address) !== 'chronicle_farm') {
      columns.splice(3, 0, {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            Claimed{' '}
            <CryptoIcon
              name={data.rewards_token_symbol}
              address={data.rewards_token_address}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => <CurrencyValue value={row.claimed} />,
        sortingField: 'claimed',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        getCellBottom: (row) => (
          <CurrencyValue
            color="muted"
            fontSize="-1"
            value={row.claimed_usd}
            options={{
              currency: 'USD'
            }}
          />
        )
      });
    }

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.urn_address}
        rowLinkProps={{
          to: (row) => row.urn_address,
          // @ts-ignore
          linkComponent: Link
        }}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    );
  }
  return <TableSkeleton />;
}
